import { Box, Button, Dialog, Grid, TextField, Typography, useTheme } from "@mui/material";
import { usePage, useSlugValidation } from "@platformx/authoring-apis";
import { PopupImage, SaveAnimationGif } from "@platformx/shared/static-assets";
import {
  LanguageDropDownCheckBox,
  MaxLengthValidationMessage,
  ShowToastError,
  ShowToastSuccess,
  findMaxCharLength,
  formatPageUrl,
  formatTitle,
  frameTitle,
} from "@platformx/utilities";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DUPLICATE_TITLE_VALIDATION,
  DUPLICATE_URL_VALIDATION,
  TITLE_VALIDATION,
  URL_VALIDATION,
} from "../../components/ContentListingContainer/helper";
import { useStyles } from "./CreateNewPage.styles";
import { DialogList } from "./CreateNewPage.types";

const PAGE_DOC_TYPE = "Sitepage";
const PAGE_DOC_TYPE_LABEL = "Page";

const CreateNewPage = ({
  isDialogOpen,
  closeButtonHandle,
  pageNameInitial = "",
  isDuplicateValue = false,
}: DialogList) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDuplicate, setIsDuplicate] = useState(isDuplicateValue);
  const [language, setLanguage] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const copiedPageName = pageNameInitial
    ? frameTitle(
        `${t("page_duplicate_placeholder")} ${pageNameInitial}`?.substring(
          0,
          DUPLICATE_TITLE_VALIDATION[0].value,
        ),
      )
    : "";
  const [pageName, setPageName] = useState(copiedPageName);
  const [pageUrl, setPageUrl] = useState(pageNameInitial ? formatPageUrl(copiedPageName) : "");
  const [showPageNameError, setShowPageNameError] = useState(false);
  const [showPageUrlError, setShowPageUrlError] = useState(false);
  const { duplicatePage } = usePage();
  const slugValidation = useSlugValidation(PAGE_DOC_TYPE);

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_url_available", { docType: PAGE_DOC_TYPE_LABEL }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_url_not_unique", { docType: PAGE_DOC_TYPE_LABEL }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  useEffect(() => {
    slugValidation.reset();
  }, [isDialogOpen]);

  const confirmButtonHandle = async (pgName, pgUrl) => {
    try {
      setLoading(true);
      await duplicatePage(isDuplicate, pgName, pgUrl, language);
      closeButtonHandle();
    } catch (err) {
      ShowToastError(t("api_error_toast"));
    } finally {
      setLoading(false);
    }
  };
  const handleConfirm = async () => {
    const pgName = pageName.trim();
    const pgUrl = pageUrl.trim();

    if (pgName.length === 0) {
      setShowPageNameError(true);
    }
    if (pgUrl.length === 0) {
      setShowPageUrlError(true);
    }
    //if no error in entered page name and url field call create page
    if (pgName.length !== 0 && pgUrl.length !== 0) {
      const isUnique = await slugValidation.checkNow(pgUrl, { docType: PAGE_DOC_TYPE });
      if (!isUnique) {
        ShowToastError(t("slug_url_not_unique", { docType: PAGE_DOC_TYPE_LABEL }));
        return;
      }
      confirmButtonHandle(pgName, pgUrl);
    }
  };

  const handleClose = (reason) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    closeButtonHandle();
  };

  const handleUrlChange = (event) => {
    if (showPageUrlError) {
      setShowPageUrlError(false);
    }
    const updatedUrl = event.target.value.replace(/[^a-zA-Z0-9-_]|[_-](?=[_-])/g, "");
    setPageUrl(updatedUrl);
    // Editing the URL invalidates any previous uniqueness result.
    slugValidation.invalidate();
  };

  const formUrlAndUpdateState = () => {
    const updatedUrl = formatPageUrl(pageUrl);
    setPageUrl(updatedUrl);
    // Slug-uniqueness check fires only on URL blur, debounced inside the hook.
    slugValidation.validate(updatedUrl, { docType: PAGE_DOC_TYPE });
  };

  const formTitleAndUpdateState = () => {
    const updatedTitle = frameTitle(pageName);
    setPageName(updatedTitle);
    slugValidation.validate(pageUrl, { docType: PAGE_DOC_TYPE });
  };

  const handlePgNameChange = (event) => {
    // const pgName = event.target.value.replace(/[^a-zA-Z0-9-_]|[_-](?=[_-])/g, "");
    const pgName = formatTitle(event.target.value);
    if (showPageNameError) {
      setShowPageNameError(false);
      setShowPageUrlError(false);
    }
    setPageName(pgName);
    setPageUrl(formatPageUrl(event.target.value));
    slugValidation.invalidate();
  };
  const classes = useStyles();
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleConfirm();
      }
    };
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });

  useEffect(() => {
    if (isDuplicateValue) {
      setIsDuplicate(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      setPageName("");
      setPageUrl("");
    };
  }, []);

  return (
    <Dialog
      fullWidth
      open={isDialogOpen}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        ".Platform-x-Dialog-paper": {
          padding: 0,
          width: { xs: "100%" },
          borderRadius: "5px",
          maxWidth: { sx: "100%", md: "700px", em: "800px" },
          margin: "15px",
        },
      }}>
      <Grid container>
        <Grid xs={12} md={6}>
          <Box className={classes.createPagePopupLeft}>
            <Box>
              <Box className={classes.createPagePopuprowBox}>
                <Typography id='alert-dialog-title' component='h2' variant='h3semibold'>
                  {isDuplicate ? t("page_duplicate_title") : t("page_create_title")}
                </Typography>
              </Box>
              <Box className={classes.createPagePopuprowBox}>
                <Typography variant='h5regular'>{t("page_title_label")}*</Typography>
                <TextField
                  autoFocus
                  value={pageName}
                  placeholder={t("page_title_placeholer")}
                  margin='dense'
                  id='name'
                  type='text'
                  data-testid='pagename'
                  fullWidth
                  variant='filled'
                  autoComplete='off'
                  error={showPageNameError}
                  sx={{
                    ".Platform-x-Input-root:after": {
                      borderBottom: `1px solid ${theme?.palette?.customColor?.PRIMARY}`,
                    },
                  }}
                  onChange={(e) => {
                    handlePgNameChange(e);
                  }}
                  onBlur={formTitleAndUpdateState}
                  inputProps={{
                    readOnly: false,
                    maxLength: findMaxCharLength(
                      isDuplicate ? DUPLICATE_TITLE_VALIDATION : TITLE_VALIDATION,
                    ),
                  }}
                />
                <MaxLengthValidationMessage
                  validations={isDuplicate ? DUPLICATE_TITLE_VALIDATION : TITLE_VALIDATION}
                  currentLength={pageName?.length || 0}
                />
                {/* {showPageNameError && (
                  <Typography
                    variant='p4regular'
                    color={theme?.palette?.notification?.ERROR?.ACTIVE_COLOR}>
                    {t("page_name_can_not_be_blank")}
                  </Typography>
                )} */}
              </Box>
              <Box className={classes.createPagePopuprowBox}>
                <Typography variant='h5regular'>{t("page_url_label")}*</Typography>
                <TextField
                  margin='dense'
                  id='name'
                  type='text'
                  data-testid='pageurl'
                  placeholder={t("page_url_placeholder")}
                  onChange={(e) => handleUrlChange(e)}
                  onBlur={formUrlAndUpdateState}
                  value={pageUrl}
                  fullWidth
                  error={showPageUrlError}
                  variant='filled'
                  autoComplete='off'
                  sx={{
                    ".Platform-x-Input-root:after": {
                      borderBottom: `1px solid ${theme?.palette?.customColor?.PRIMARY}`,
                    },
                  }}
                  inputProps={{
                    readOnly: false,
                    maxLength: findMaxCharLength(
                      isDuplicate ? DUPLICATE_URL_VALIDATION : URL_VALIDATION,
                    ),
                  }}
                />
                <MaxLengthValidationMessage
                  validations={isDuplicate ? DUPLICATE_URL_VALIDATION : TITLE_VALIDATION}
                  currentLength={pageUrl?.length || 0}
                />
                {/* {showPageUrlError && (
                  <Typography
                    variant='p4regular'
                    color={theme?.palette?.notification?.ERROR?.ACTIVE_COLOR}>
                    {t("page_url_can_not_be_blank")}
                  </Typography>
                )} */}
              </Box>
              {isDuplicate && (
                <Box className={classes.createPagePopuprowBox} sx={{ display: "none" }}>
                  <Typography variant='h5regular'>{t("page_language_label")}</Typography>
                  <LanguageDropDownCheckBox language={language} setLanguage={setLanguage} />
                </Box>
              )}
            </Box>
            <Box className={classes.createPagePopupButtonWp}>
              <Button
                disableElevation
                variant='secondaryButton'
                onClick={closeButtonHandle}
                data-testid='create-new-page-close-btn'>
                {t("back")}
              </Button>
              <Button
                variant='primaryButton'
                data-testid='new-page-done'
                disabled={
                  isDuplicate &&
                  (pageName.trim().length === 0 ||
                  pageUrl.trim().length === 0 ||
                  language?.length === 0
                    ? true
                    : false)
                }
                disableElevation
                onClick={handleConfirm}>
                {loading && <img src={SaveAnimationGif} alt='' />}
                {loading ? t("creating") : t("create")}
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid xs={12} md={6} className={classes.createPagePopupRight}>
          <Box className={classes.popupRightImage}>
            <img src={PopupImage} alt='Create Page Popup' />
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};
export default CreateNewPage;
